import { tw } from "@careinfo/tailwind";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { Children, Dispatch, Fragment, SetStateAction } from "react";

interface Props {
  isOpen: boolean;
  children: React.ReactNode;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  closeModal?: () => void;
  title?: string;
  width?: string;
}

const Modal = ({
  isOpen,
  setIsOpen,
  children,
  title,
  closeModal,
  width = "fit-content",
}: Props) => {
  const caution = Children.toArray(children).find(
    (child: any) => child.type === Modal.Caution
  );

  const otherChildren = Children.toArray(children).filter(
    (child: any) => child.type !== Modal.Caution
  );

  function close() {
    if (closeModal) {
      closeModal();
      if (isOpen && setIsOpen) setIsOpen(false);
    } else if (setIsOpen) {
      setIsOpen(false);
    } else {
      throw new Error("No closing mechanism provided from Modal");
    }
  }

  return (
    <div>
      <Transition appear show={isOpen} as={Fragment}>
        <Dialog as="div" className={tw`relative z-50`} onClose={close}>
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className={tw`fixed inset-0 bg-black bg-opacity-25`} />
          </TransitionChild>

          <div className={tw`fixed inset-0 overflow-y-auto`}>
            <div
              className={tw`flex min-h-full items-center justify-center p-4 text-center`}
            >
              <TransitionChild
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <DialogPanel
                  className={tw`max-w-4xl transform  rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all`}
                  style={{
                    width,
                  }}
                >
                  {title && (
                    <DialogTitle
                      as="h3"
                      className={tw`text-lg font-medium leading-6 text-gray-900 flex justify-between gap-x-4`}
                    >
                      <p className={tw`font-semibold`}>{title}</p>
                      <button
                        className={tw`cursor-pointer focus:outline-none`}
                        onClick={close}
                      >
                        <svg
                          width="24"
                          height="24"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M18 6L6 18M6 6L18 18"
                            stroke="#0D0D0D"
                            stroke-width="2"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </svg>
                      </button>
                    </DialogTitle>
                  )}
                  {caution}
                  {otherChildren}
                </DialogPanel>
              </TransitionChild>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
};

Modal.Caution = ({ children }: { children: React.ReactNode }) => {
  return <>{children}</>;
};

export default Modal;
